import { Component, EventEmitter, OnDestroy, Output } from '@angular/core';
import { PasswordField } from "../../ui/password-field/password-field";
import { TranslatePipe } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { CommonModule } from '@angular/common';
import { Button } from "../../ui/button/button";
import { RuleKey } from '../../utils/types';

@Component({
  selector: 'app-password-change',
  standalone: true,
  imports: [PasswordField, TranslatePipe, Button, CommonModule, ReactiveFormsModule],
  templateUrl: './password-change.html',
  styleUrl: './password-change.css'
})
export class PasswordChange implements OnDestroy {
  @Output() submitAction = new EventEmitter<{ newPassword: string, confirmPassword: string }>();
  private destroy$ = new Subject<void>();

  changePasswordForm: FormGroup;

  rules: Record<RuleKey, boolean> = {
    minLength: false,
    capital: false,
    number: false,
    special: false
  };

  ruleList = [
    { key: 'minLength' as RuleKey, label: 'CHANGE_PASSWORD.MINIMUM_CHARACTERS' },
    { key: 'capital' as RuleKey, label: 'CHANGE_PASSWORD.CAPITAL_LETTER' },
    { key: 'number' as RuleKey, label: 'CHANGE_PASSWORD.NUMBER' },
    { key: 'special' as RuleKey, label: 'CHANGE_PASSWORD.SPECIAL_CHARACTER' }
  ];

  icons = {
    muted: 'assets/icons/check-muted.svg',
    valid: 'assets/icons/check-success.svg',
    invalid: 'assets/icons/cancel-danger.svg',
  };

  constructor(private fb: FormBuilder) {
    this.changePasswordForm = this.fb.group({
      newPassword: ['', Validators.required],
      confirmPassword: ['', Validators.required]
    });

    this.changePasswordForm
      .get('newPassword')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(value => this.validatePassword(value));
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onConfirmPasswordChange(value: string) {
    this.changePasswordForm.get('confirmPassword')?.setValue(value);
  }

  validatePassword(pwd: string) {
    this.rules.minLength = pwd.length >= 8;
    this.rules.capital = /[A-Z]/.test(pwd);
    this.rules.number = /\d/.test(pwd);
    this.rules.special = /[^A-Za-z0-9]/.test(pwd);
  }

  getRuleStatus(rule: RuleKey): boolean {
    return this.rules[rule];
  }

  get passwordsMatch(): boolean {
    const { newPassword, confirmPassword } = this.changePasswordForm.value;
    return newPassword && confirmPassword && newPassword === confirmPassword;
  }

  get isFormValid(): boolean {
    return (
      this.rules.minLength &&
      this.rules.capital &&
      this.rules.number &&
      this.rules.special &&
      this.passwordsMatch
    );
  }

  submitHandler() {
    if (this.isFormValid) {
      const { newPassword, confirmPassword } = this.changePasswordForm.value;
      this.submitAction.emit({ newPassword, confirmPassword });
    }
  }

  get newPasswordControl(): FormControl {
    return this.changePasswordForm.get('newPassword') as FormControl;
  }
  get confirmPasswordControl(): FormControl {
    return this.changePasswordForm.get('confirmPassword') as FormControl;
  }

}
